import React, { useContext, useEffect } from 'react'
import { TeamContextProvider } from '../pages/private/data/team'
import fetchApi from '../api'
import { omit } from 'lodash'
import { useNavigate } from 'react-router-dom'
export const userApi = {
    login: ({ email, password, captcha }) =>
        fetchApi('/users/login', {
            method: 'post',
            body: JSON.stringify({ email, password, captcha }),
        }),
    create: (user) =>
        fetchApi('/users', {
            method: 'post',
            body: JSON.stringify(user),
        }),
    get: ({ _id }) => fetchApi('/users/' + _id),
    update: (user, activeUser) =>
        fetchApi(`/users/${activeUser._id}`, {
            method: 'put',
            body: omit(user, ['teams', 'createdAt']),
        }),
    acceptInvitation: (userId, token) =>
        fetchApi(`/users/${userId}/teams/invitations/${token}`, {
            method: 'put',
        }),
    requestPasswordReset: ({ email, captcha }) =>
        fetchApi(`/users/request-password`, {
            method: 'post',
            body: { email, captcha },
        }),
    resetPassword: (newPassword, token) =>
        fetchApi('/users/password-reset/' + token, {
            method: 'post',
            body: newPassword,
        }),
}

export const UserContext = React.createContext()
const STORAGE_KEY = 'foobal_user'
const STORAGE_TYPE = 'localStorage' // 'sessionStorage'
const getFromStorage = () =>
    JSON.parse(window[STORAGE_TYPE].getItem(STORAGE_KEY) || 'null')
const setOnStorage = (value) =>
    value
        ? window[STORAGE_TYPE].setItem(STORAGE_KEY, JSON.stringify(value))
        : window[STORAGE_TYPE].removeItem(STORAGE_KEY)

export const useUser = (initialQueries = []) => {
    const userContext = useContext(UserContext)
    return [{ userContext, ...userContext }]
}

export const UserContextProvider = (props) => {
    const [activeUser, _setActiveUser] = React.useState(getFromStorage())
    const [logout, setLogout] = React.useState(false)
    const navigate = useNavigate()
    const setActiveUser = (user) => {
        if (!user?._id) {
            _setActiveUser()
            return
        }
        if (logout) return
        _setActiveUser((initialUser) => ({
            ...user,
            teams: user?.teams?.length ? user?.teams : initialUser?.teams,
        }))

        return user
    }
    useEffect(() => {
        if (logout) return
        setOnStorage(activeUser)
    }, [activeUser])
    useEffect(() => {
        if (!logout) {
        } else {
            fetchApi(`/users/${activeUser._id}/logout`, {
                method: 'post',
            })
                .then(async () => {
                    setLogout(true)
                    setActiveUser()
                    // Clera localstorage
                    // Clear cache
                    caches.keys().then(function (names) {
                        for (let name of names) caches.delete(name)
                    })
                    await navigator.serviceWorker
                        .getRegistrations()
                        .then((registrations) => {
                            registrations.forEach((registration) => {
                                registration.unregister()
                            })
                        })
                        .catch(console.error)
                    localStorage.clear()
                    sessionStorage.clear()
                    // Clear cache
                })

                .then(() => {
                    setLogout(false)
                    navigate('/login')
                })
        }
    }, [logout])
    const api = {
        login: (loginObj) => userApi.login(loginObj).then(setActiveUser),
        socialAuth: ({ provider, data }) =>
            fetchApi('/users/login', {
                method: 'post',
                body: JSON.stringify({
                    email: data.email,
                    provider,
                    token: data.credential,
                }),
            }).then(setActiveUser),
        socialAuthSignup: ({ provider, data }) =>
            fetchApi('/users/social-signup', {
                method: 'post',
                body: JSON.stringify({
                    provider: provider,
                    email: data.email,
                    token: data.credential,
                }),
            }).then(setActiveUser),
        logout: () => setLogout(true),
        create: (user) => userApi.create(user),
        get: (user) => userApi.get(user).then(setActiveUser),
        acceptInvitation: (token) =>
            userApi.acceptInvitation(activeUser._id, token),
        update: (newUser) =>
            userApi.update(newUser, activeUser).then(setActiveUser),
        requestPasswordReset: (body) => userApi.requestPasswordReset(body),
        resetPassword: (password, token) =>
            userApi.resetPassword(password, token),
        pushSubscribe: (subscription, options) =>
            fetchApi(`/users/${activeUser._id}/push/subscribe`, {
                method: 'post',
                body: { subscription, options },
            }),
        pushUnsubscribe: (subscription) =>
            fetchApi(`/users/${activeUser._id}/push/unsubscribe`, {
                method: 'post',
                body: { subscription },
            }),
        followTeam: (teamId) =>
            fetchApi(`/users/${activeUser._id}/followTeam`, {
                method: 'put',
                body: { teamId },
            }).then(setActiveUser),
        unfollowTeam: (teamId) =>
            fetchApi(`/users/${activeUser._id}/unfollowTeam`, {
                method: 'put',
                body: { teamId },
            }).then(setActiveUser),
    }

    useEffect(() => {
        if (logout) return
        activeUser?._id?.length === 24 && api.get(activeUser)
    }, [activeUser?._id])
    return (
        <UserContext.Provider value={{ activeUser, setActiveUser, api }}>
            {!activeUser?._id ? (
                props.children
            ) : (
                <TeamContextProvider user={activeUser}>
                    {props.children}
                </TeamContextProvider>
            )}
        </UserContext.Provider>
    )
}

export default UserContext
